import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RouteIcon from '@mui/icons-material/Route';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LockIcon from '@mui/icons-material/Lock';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FlagIcon from '@mui/icons-material/Flag';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import agent from '../../agent';
import useTranslation from '../../customHooks/translations';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import { CREATE_TRACK_FEATURE } from '../../constants/featureFlags';
import { getLatLon, getMapObject } from '../../utils/freakUtils';
import { buildQuickFreak, buildQuickFreakTitle, withMapTitle } from '../../utils/quickCreate';
import BlastUploadDialog from '../Blasts/BlastUploadDialog';

const BLAST_COLOR = '#ff3d00';
const SHEET_BG = '#111';
const CARD_BG = '#1c1c1e';

const Chip = ({ icon: ChipIcon, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#2a2a2c', borderRadius: 2.5, px: 1, py: 0.25 }}>
    <ChipIcon sx={{ fontSize: 13, color: '#aaa' }}/>
    <Typography sx={{ color: '#ccc', fontSize: 12 }}>{label}</Typography>
  </Box>
);

// Quick start behind the top bar "+" on the freaks feed: record a track or post a blast in one click,
// the same actions as a freak's track / blast buttons - the freak they belong to is created for you.
// renderTrigger(open) swaps the default "+" for the caller's own button.
// Mirrors mobile components/QuickCreate/QuickCreateButton.tsx.
export const QuickCreateButton = ({ currentUser, context, renderTrigger }) => {
  const translation = useTranslation();
  const q = translation.quickCreate;
  const navigate = useNavigate();
  const showCreateTrack = useFeatureFlagEnabled(CREATE_TRACK_FEATURE);
  const [open, setOpen] = useState(false);
  const [busyKind, setBusyKind] = useState(null);
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [blastFreak, setBlastFreak] = useState(null);

  if (!currentUser?.username) return null;

  const openSheet = () => {
    setTitle('');
    setError('');
    setBusyKind(null);
    setOpen(true);
  };

  const closeSheet = () => {
    if (!busyKind) setOpen(false);
  };

  const locate = async () => {
    try {
      const map = await getMapObject();
      return { map, position: [map.coordinates.latitude, map.coordinates.longitude] };
    } catch (e) {
      // geocoding failed - still pin the freak on the raw coordinates
      const { latitude, longitude } = await getLatLon();
      return { map: withMapTitle(null, latitude, longitude), position: [latitude, longitude] };
    }
  };

  const start = async (kind) => {
    if (busyKind) return;
    setBusyKind(kind);
    setError('');
    let located;
    try {
      located = await locate();
    } catch (e) {
      setError(q.locationNeeded);
      setBusyKind(null);
      return;
    }
    try {
      const { latitude, longitude } = located.map.coordinates;
      const payload = buildQuickFreak({
        title: title.trim() || buildQuickFreakTitle(q[kind].defaultTitle, new Date()),
        context: context || 'wof',
        map: withMapTitle(located.map, latitude, longitude),
        startDate: new Date(),
      });
      const { freak } = await agent.Freaks.create(payload);
      setOpen(false);
      if (kind === 'track') {
        navigate('/record', { state: { freak, position: located.position } });
      } else {
        setBlastFreak(freak);
      }
    } catch (e) {
      setError(q.somethingWentWrong);
    } finally {
      setBusyKind(null);
    }
  };

  const actions = [
    { kind: 'track', Icon: RouteIcon, color: DEFAULT_THEME_COLOR, disabled: showCreateTrack === false },
    { kind: 'blast', Icon: LocalFireDepartmentIcon, color: BLAST_COLOR, disabled: false },
  ];

  return (
    <>
      {renderTrigger ? renderTrigger(openSheet) : <IconButton
        data-testid="quick-create-button"
        aria-label={q.buttonA11y}
        onClick={openSheet}
        sx={{
          bgcolor: DEFAULT_THEME_COLOR, color: 'white', width: 34, height: 34, flexShrink: 0, ml: 1,
          '&:hover': { bgcolor: DEFAULT_THEME_COLOR, filter: 'brightness(1.1)' },
        }}
      >
        <AddIcon/>
      </IconButton>}

      <Dialog
        open={open}
        onClose={closeSheet}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { bgcolor: SHEET_BG, color: 'white', borderRadius: 4, p: 2.5, m: 2 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{q.title}</Typography>
            <Typography sx={{ color: '#999', mt: 0.5 }}>{q.subtitle}</Typography>
          </Box>
          <IconButton aria-label={q.close} onClick={closeSheet} sx={{ color: '#777', mt: -1, mr: -1 }}>
            <CloseIcon/>
          </IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 2 }}>
          {actions.map(({ kind, Icon, color, disabled }) => {
            const busy = busyKind === kind;
            const dimmed = disabled || (busyKind && !busy);
            return (
              <ButtonBase
                key={kind}
                data-testid={`quick-create-${kind}`}
                disabled={disabled || !!busyKind}
                onClick={() => start(kind)}
                sx={{
                  flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', p: 2, minHeight: 156,
                  bgcolor: CARD_BG, borderRadius: 4.5, border: '1.5px solid', borderColor: busy ? color : '#2c2c2e',
                  opacity: dimmed ? 0.45 : 1, transition: 'transform .12s, border-color .12s',
                  '&:hover': { borderColor: color, transform: 'translateY(-2px)' },
                  '&:active': { transform: 'scale(0.97)' },
                }}
              >
                <Box sx={{
                  width: 56, height: 56, borderRadius: '50%', bgcolor: `${color}26`, mb: 1.75,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {busy
                    ? <CircularProgress size={24} sx={{ color }}/>
                    : disabled ? <LockIcon sx={{ fontSize: 30, color }}/> : <Icon sx={{ fontSize: 30, color }}/>}
                </Box>
                <Typography sx={{ fontSize: 17, fontWeight: 700 }}>{q[kind].title}</Typography>
                <Typography sx={{ color: '#999', fontSize: 13, mt: 0.5 }}>
                  {busy ? q.creating : disabled ? q.track.locked : q[kind].hint}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>

        <Box sx={{ bgcolor: CARD_BG, borderRadius: 3.5, p: 1.75, mt: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FlagIcon sx={{ fontSize: 16, color: DEFAULT_THEME_COLOR }}/>
            <Typography sx={{ color: '#aaa', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              {q.freakLabel}
            </Typography>
          </Box>
          <InputBase
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={!!busyKind}
            placeholder={q.titlePlaceholder}
            inputProps={{ maxLength: 80, 'data-testid': 'quick-create-title' }}
            sx={{
              width: '100%', color: 'white', fontSize: 16, fontWeight: 600, py: 0.5, borderBottom: '1px solid #333',
              '& input::placeholder': { color: '#777', opacity: 1 },
            }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
            <Chip icon={LockIcon} label={q.private}/>
            <Chip icon={MyLocationIcon} label={q.here}/>
            <Chip icon={ScheduleIcon} label={q.now}/>
          </Box>
        </Box>

        {error ? <Typography data-testid="quick-create-error" sx={{ color: '#ff6b6b', mt: 1.5, fontSize: 14 }}>{error}</Typography> : null}

        <ButtonBase
          onClick={() => {
            setOpen(false);
            navigate('/freak-editor');
          }}
          disabled={!!busyKind}
          sx={{ alignSelf: 'center', mt: 2, py: 0.75, color: DEFAULT_THEME_COLOR, fontWeight: 700, gap: 0.25 }}
        >
          {q.fullEditor}
          <ChevronRightIcon sx={{ fontSize: 18 }}/>
        </ButtonBase>
      </Dialog>

      <BlastUploadDialog
        open={!!blastFreak}
        freakId={blastFreak?.id}
        onClose={() => setBlastFreak(null)}
        onSuccess={() => {
          const slug = blastFreak?.slug;
          setBlastFreak(null);
          if (slug) navigate(`/freak/${slug}`);
        }}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  currentUser: state.common.currentUser,
  context: state.common.context,
});

export default connect(mapStateToProps)(QuickCreateButton);
